import { authService } from '@/services/auth.service';

// Property type mapping
const propertyTypeMap = {
  AP: 'Apartment',
  BW: 'Bungalow',
  CD: 'Compound',
  DX: 'Duplex',
  FF: 'Full floor',
  HF: 'Half floor',
  LP: 'Land',
  PH: 'Penthouse',
  TH: 'Townhouse',
  VH: 'Villa',
  WB: 'Whole Building',
  LC: 'Labor Camp',
  WH: 'Warehouse',
  FA: 'Factory',
  OF: 'Office',
  RE: 'Retail',
  SH: 'Shop',
  SR: 'Show Room',
};

// Offering type mapping
const offeringTypeMap = {
  RS: 'For Sale',
  CR: 'For Rent',
};

export const PropertyService = {
  // Fetch all properties from admin panel
  getAllProperties: async () => {
    try {
      const response = await authService.getProperties();
      if (response?.data?.items?.length) return response.data.items;
      return [];
    } catch (error) {
      console.error('Failed to fetch properties:', error);
      return [];
    }
  },

  // Fetch PF listings already normalized to admin-panel shape
  // type: 'buy' | 'rent' | 'offplan'
  getPFProperties: async (type) => {
    try {
      const base =
        typeof window === 'undefined'
          ? process.env.NEXT_PUBLIC_SITE_URL || 'http://localhost:3000'
          : '';
      const res = await fetch(`${base}/api/pf-listings?type=${type}`);
      if (!res.ok) throw new Error(`PF listings returned ${res.status}`);
      const data = await res.json();
      return data.listings || [];
    } catch (error) {
      console.error('Failed to fetch PF properties:', error);
      return [];
    }
  },

  searchProperties: async (query, type, beds, baths, offeringType) => {
    try {
      const allProperties = await PropertyService.getAllProperties();

      const isBuy = offeringType === 'buy';
      const isOffPlan = offeringType === 'new projects';
      const isRent = offeringType === 'rent';
      const isCommercial = offeringType === 'commercial';

      return allProperties.filter((property) => {
        const offeringTypeValue = property?.offering_type?.[0];
        const completionStatus = property?.completion_status?.[0];

        // Query match
        const matchesQuery =
          !query ||
          property?.title_en?.[0]
            ?.toLowerCase()
            .includes(query?.toLowerCase()) ||
          property?.community?.[0]
            ?.toLowerCase()
            .includes(query?.toLowerCase()) ||
          (property?.sub_community?.[0] &&
            property?.sub_community?.[0]
              ?.toLowerCase()
              .includes(query?.toLowerCase()));

        // Type match
        const propertyTypeCode = Object.keys(propertyTypeMap).find(
          (key) => propertyTypeMap[key] === type
        );
        const matchesType =
          !type || property.property_type?.[0] === propertyTypeCode;

        // Beds match
        const matchesBeds =
          !beds?.length ||
          beds.some((bed) => {
            if (bed === 'Studio') return property.bedroom?.[0] === '0';
            if (bed === '5+')
              return parseInt(property.bedroom?.[0] || '0') >= 5;
            return property.bedroom?.[0] === bed;
          });

        // Baths match
        const matchesBaths =
          !baths?.length ||
          baths.some((bath) => {
            if (bath === '5+')
              return parseInt(property.bathroom?.[0] || '0') >= 5;
            return property.bathroom?.[0] === bath;
          });

        // Your custom offering logic
        const matchesOffering =
          (isBuy &&
            ['RS', 'CS'].includes(offeringTypeValue) &&
            !['off plan', 'off_plan_primary'].includes(completionStatus)) ||
          (isOffPlan &&
            ['off plan', 'off_plan_primary'].includes(completionStatus) &&
            offeringTypeValue === 'RS') ||
          ((isRent || isCommercial) &&
            ['RR', 'CR'].includes(offeringTypeValue));

        return (
          matchesQuery &&
          matchesType &&
          matchesBeds &&
          matchesBaths &&
          matchesOffering
        );
      });
    } catch (error) {
      console.error('Error searching properties:', error);
      return [];
    }
  },

  // Get location suggestions based on query
  getLocationSuggestions: async (query) => {
    try {
      const allProperties = await PropertyService.getAllProperties();

      const locations = new Set();

      allProperties.forEach((property) => {
        if (property.community?.[0]) {
          locations.add(property.community?.[0]);
        }
        if (property.sub_community?.[0]) {
          locations.add(property.sub_community?.[0]);
        }
        if (property.city?.[0]) {
          locations.add(`${property.community?.[0]}, ${property.city?.[0]}`);
        }
      });

      return Array.from(locations)
        .filter((location) =>
          location?.toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, 10);
    } catch (error) {
      console.error('Error getting location suggestions:', error);
      return [];
    }
  },

  // Format property data for display
  formatPropertyForDisplay: (property) => {
    return {
      id: property.id || property?.reference_number?.[0] || '',
      title: property?.title_en?.[0],
      location: property?.sub_community?.[0]
        ? `${property.sub_community}, ${property?.community?.[0]}`
        : property.community?.[0],
      type:
        propertyTypeMap[property?.property_type?.[0]] || property.property_type,
      price: `AED ${parseInt(property?.price?.[0]).toLocaleString()}`,
      image:
        `${property.photo_url}?height=200&width=300` ||
        '/placeholder.svg?height=200&width=300',
      description: property?.description_en?.[0],
      beds: property?.bedroom?.[0] ? parseInt(property.bedroom) : undefined,
      baths: property?.bathroom?.[0] ? parseInt(property.bathroom) : undefined,
      area: property?.size?.[0] ? parseInt(property.size) : undefined,
      slug: property?.slug ? property.slug : '',
      completion_status: property?.completion_status?.[0],
      offering_type: property?.offering_type?.[0],
      status:
        offeringTypeMap[property?.offering_type?.[0]] ||
        property?.offering_type?.[0],
    };
  },
};
